import { useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PunchType, checkManualPunches, type ManualPunchInput, type ManualPunchProblem } from '@construction/shared';
import { attendanceApi, sitesApi, type Punch, type Site } from '@/api/endpoints';
import { Alert, Button, Checkbox, FormActions, Input, Select, Spinner } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { cn } from '@/lib/cn';
import { formatClock, formatWallClock, splitHours } from '@/lib/format';
import { siteDateKey, siteDayRange, siteLocalToIso } from '@/lib/site-time';
import {
  defaultEndDate,
  defaultFreeKind,
  defaultStartDate,
  flipHalfDay,
  fromMinutes,
  localDateAndMinutes,
  openClockIn,
  orphanClockOut,
  punchAfter,
  punchBefore,
  shiftEndDate,
  toMinutes,
  toPunchLike,
  type ContextPunch,
  type FreeKind,
} from './backfill-rules';
import { invalidateAttendance } from './invalidate';

/**
 * 补卡从哪里进：
 * - `anomaly`：「异常」里点「补下班卡 / 补上班卡」—— 人、补哪种、对着哪张卡都已经定了；
 *   班次过长时带 `alsoResolve`：补完下班卡后，把那张迟到的下班卡一起标记已处理；
 * - `free`：页头「补录打卡」—— 先选人，再选补下班卡 / 补上班卡 / 补整段（按他的打卡猜好默认）。
 */
export type BackfillTarget =
  | { mode: 'anomaly'; userId: number; type: PunchType; anchor: Punch; alsoResolve?: Punch }
  | { mode: 'free' };

type ReasonKey = 'forgot' | 'battery' | 'location' | 'other';

const REASONS: ReadonlyArray<{ key: ReasonKey; label: string }> = [
  { key: 'forgot', label: 'attendance.reasonForgot' },
  { key: 'battery', label: 'attendance.reasonBattery' },
  { key: 'location', label: 'attendance.reasonLocation' },
  { key: 'other', label: 'attendance.reasonOther' },
];

/** 常见整点：上班早上、下班傍晚 */
const FIXED_TIMES: Record<PunchType, string[]> = {
  [PunchType.IN]: ['07:00', '07:30', '08:00'],
  [PunchType.OUT]: ['17:00', '17:30', '18:00'],
};

const KIND_LABEL: Record<FreeKind, string> = {
  out: 'attendance.addOut',
  in: 'attendance.addIn',
  shift: 'attendance.addShift',
};

const PROBLEM_LABEL: Record<Exclude<ManualPunchProblem, 'tooLong'>, string> = {
  future: 'attendance.checkFuture',
  order: 'attendance.checkOrder',
  overlap: 'attendance.checkOverlap',
  noOpenIn: 'attendance.checkNoOpenIn',
  noOpenOut: 'attendance.checkNoOpenOut',
};

/**
 * 一端（上班或下班）填了什么。`null` 是「用默认值」：
 * 时间没动就用建议的；日期没动就跟着时间推（夜班推到次日），动过就保留管理员选的。
 */
interface MomentInput {
  date: string | null;
  time: string | null;
}

const UNTOUCHED: MomentInput = { date: null, time: null };

/** 一个可点的选项（时间、理由、人、补哪种）：整颗是命中区，不低于 44px */
function Choice({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'flex min-h-touch items-center rounded-full border px-3.5 text-body transition-colors',
        selected
          ? 'border-brand-600 bg-brand-50 font-medium text-brand-700'
          : 'border-rule text-ink-2 hover:bg-surface-2',
      )}
    >
      {children}
    </button>
  );
}

/** 日期型 key 按界面语言显示：`9/14 周一`（日期型按 UTC 渲染，见 CLAUDE.md 代码约定） */
function dayLabel(date: string, lang: string, weekday = true): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(lang, {
    timeZone: 'UTC',
    month: 'numeric',
    day: 'numeric',
    ...(weekday ? { weekday: 'short' } : {}),
  });
}

/**
 * 补录打卡（考勤页 UI 打磨，替换原来的「补录表单」；CR-066 改成日期 + 时间都摆出来、能补整段）。
 *
 * 客户原话「补录打卡这个也需要优化，现在操作繁琐了点。以及在异常里点击补录打卡需要智能一点」；
 * CR-066 原话「考勤的补录系统中，没有日期选择，只有时间选择。这个会导致一些次日打卡的问题」。
 *
 * 表单画的是**一段班次**：上班、下班两端，已有的那端只读（带日期），要补的那端是「日期 + 时间」两个框，
 * 下面一行写清楚整段「9/13 周日 23:00 → 9/14 周一 07:00 · 8 小时 0 分，算 9/13 的工时」。
 * - **能猜的都猜好**：人、补哪种、建议时间（「他平时」来自 `backfill-context`），日期按规则默认（夜班填次日）；
 * - **时间框点开就是系统时钟**（App 里 `<input type="time">` 弹安卓时钟，电脑上打字），下面一排选项一点就选；
 * - **理由点选**，「其他」才要打字；
 * - **当场检查**用 shared 的 `checkManualPunches`（服务端建卡之前也查它）：未来时刻、下班早于上班、
 *   超过最长班次、和已有的卡重叠；多半点反了上午下午时给「是不是下午 5:05？」一键改；
 * - **结束登录只在有用时出现**：补的正是他此刻开着的那一班；补以前的日子不踢他现在的登录（CR-006）；
 * - 提交按钮写完整动作「补 9/14 07:00 下班卡」。
 */
export function BackfillForm({
  site,
  target,
  onClose,
}: {
  site: Site;
  target: BackfillTarget;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const tz = site.timezone;
  const today = siteDateKey(tz);
  const yesterday = siteDateKey(tz, -1);
  const anomaly = target.mode === 'anomaly' ? target : null;
  const free = anomaly === null;

  const [userId, setUserId] = useState<number | null>(anomaly ? anomaly.userId : null);
  const [freeKind, setFreeKind] = useState<FreeKind | null>(null);
  const [start, setStart] = useState<MomentInput>(UNTOUCHED);
  const [end, setEnd] = useState<MomentInput>(UNTOUCHED);
  const [reason, setReason] = useState<ReasonKey>('forgot');
  const [otherNote, setOtherNote] = useState('');
  const [endSession, setEndSession] = useState(true);
  const [alsoResolve, setAlsoResolve] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 上下文按哪天取：异常里是那张卡那天；页头是正在填的那一端的日期，没动过就是今天。
  // 服务端前后各多给一个最长班次，夜班前一晚、第二天早上的卡都在里面
  const contextDate =
    (anomaly ? localDateAndMinutes(anomaly.anchor.occurredAt, tz).date : null) ?? start.date ?? end.date ?? today;

  const context = useQuery({
    queryKey: ['backfill-context', site.id, userId, contextDate],
    queryFn: () => attendanceApi.backfillContext(site.id, userId!, contextDate),
    enabled: userId !== null,
  });

  // 页头进来才要选人：今天在岗的排前面（昨天到今天的汇总里 onShiftSince 不为空的）
  const members = useQuery({
    queryKey: ['member-options', site.id],
    queryFn: () => sitesApi.memberOptions(site.id),
    enabled: free,
  });
  const onShift = useQuery({
    queryKey: ['attendance-summary', site.id, yesterday, today],
    queryFn: () => attendanceApi.summary(site.id, siteDayRange(yesterday, today, tz)),
    enabled: free,
  });
  const onShiftPeople = (onShift.data?.people ?? [])
    .filter((person) => person.onShiftSince !== null)
    .sort((a, b) => (a.onShiftSince! < b.onShiftSince! ? -1 : 1));

  const now = new Date();
  const punches: ContextPunch[] = context.data?.punches ?? [];
  const maxShiftMinutes = context.data?.maxShiftMinutes ?? site.maxShiftMinutes;
  const dayStart = new Date(siteLocalToIso(contextDate, '00:00', tz));
  const kind: FreeKind = anomaly
    ? anomaly.type === PunchType.OUT
      ? 'out'
      : 'in'
    : (freeKind ?? defaultFreeKind(punches, now, dayStart));

  // 默认值依据的那张卡：异常里就是那张；页头补下班对着他开着的上班卡，补上班对着缺上班卡的那张下班卡
  const baseAnchor: ContextPunch | null = anomaly
    ? anomaly.anchor
    : kind === 'out'
      ? openClockIn(punches, now)
      : kind === 'in'
        ? orphanClockOut(punches, dayStart)
        : null;

  const usual = context.data?.usual ?? null;
  const nowTime = fromMinutes(localDateAndMinutes(now.toISOString(), tz).minutes);
  const isPast = (date: string, time: string) => Date.parse(siteLocalToIso(date, time, tz)) <= now.getTime();
  const valid = (date: string, time: string) => /^\d{4}-\d{2}-\d{2}$/.test(date) && toMinutes(time) !== null;
  const usualIn = usual?.clockIn?.time ?? '08:00';
  const usualOut = usual?.clockOut?.time ?? '17:00';

  // ---- 上班那一端（补下班卡时是已有的卡，这里的值不用）
  const startTime = start.time ?? usualIn;
  const startDateFor = (time: string): string => {
    if (kind === 'in' && baseAnchor) return defaultStartDate(baseAnchor.occurredAt, time, tz);
    if (kind === 'shift') {
      // 补整段默认补今天这一班；今天这一班还没下班，就默认昨天
      const endTime = end.time ?? usualOut;
      return isPast(shiftEndDate(today, time, endTime), endTime) ? today : yesterday;
    }
    return isPast(today, time) ? today : yesterday;
  };
  const startDate = start.date ?? startDateFor(startTime);

  // ---- 下班那一端（补上班卡时是已有的卡）
  const endDateFor = (time: string): string =>
    kind === 'shift'
      ? shiftEndDate(startDate, startTime, time)
      : baseAnchor
        ? defaultEndDate(baseAnchor.occurredAt, time, tz)
        : today;
  let endTime = end.time ?? usualOut;
  let endDate = end.date ?? endDateFor(endTime);
  // 补的是他这会儿开着的那一班、「平时」的下班时间还没到：默认填现在
  if (kind === 'out' && end.time === null && end.date === null && !isPast(endDate, endTime)) {
    endTime = nowTime;
    endDate = endDateFor(nowTime);
  }

  const startIso = kind !== 'out' && valid(startDate, startTime) ? siteLocalToIso(startDate, startTime, tz) : null;
  const endIso = kind !== 'in' && valid(endDate, endTime) ? siteLocalToIso(endDate, endTime, tz) : null;
  const ready = (kind === 'out' || startIso !== null) && (kind === 'in' || endIso !== null);

  const additions = (s: string | null, e: string | null): ManualPunchInput[] =>
    kind === 'shift'
      ? [
          { type: PunchType.IN, at: new Date(s!) },
          { type: PunchType.OUT, at: new Date(e!) },
        ]
      : kind === 'out'
        ? [{ type: PunchType.OUT, at: new Date(e!) }]
        : [{ type: PunchType.IN, at: new Date(s!) }];
  const existing = punches.map(toPunchLike);
  const checkWith = (s: string | null, e: string | null) =>
    checkManualPunches(existing, additions(s, e), { maxShiftMinutes, now });
  const problem = context.isSuccess && ready ? checkWith(startIso, endIso) : null;

  // 上午 / 下午点反了（5:05 当成了 17:05）：正在填的那一端换到另外半天，日期照规则重推，合法就给建议
  const suggestion = ((): string | null => {
    if (problem === null || problem === 'future' || problem === 'overlap') return null;
    if (kind === 'in') {
      const flipped = flipHalfDay(startTime);
      const date = start.date ?? startDateFor(flipped);
      return valid(date, flipped) && checkWith(siteLocalToIso(date, flipped, tz), endIso) === null ? flipped : null;
    }
    const flipped = flipHalfDay(endTime);
    const date = end.date ?? endDateFor(flipped);
    return valid(date, flipped) && checkWith(startIso, siteLocalToIso(date, flipped, tz)) === null ? flipped : null;
  })();

  // 已有的那一端：异常里就是那张卡；页头按填的时刻找紧挨着的那张（日期改了，它跟着换）
  const fixedStart =
    kind !== 'out' ? null : anomaly ? anomaly.anchor : endIso ? punchBefore(punches, endIso, PunchType.IN) : null;
  const fixedEnd =
    kind !== 'in' ? null : anomaly ? anomaly.anchor : startIso ? punchAfter(punches, startIso, PunchType.OUT) : null;

  const moment = (iso: string) => `${dayLabel(localDateAndMinutes(iso, tz).date, lang)} ${formatClock(iso, tz, lang)}`;
  const shortMoment = (iso: string) =>
    `${dayLabel(localDateAndMinutes(iso, tz).date, lang, false)} ${formatClock(iso, tz, lang)}`;
  const shiftStart = kind === 'out' ? (fixedStart?.occurredAt ?? null) : startIso;
  const shiftEnd = kind === 'in' ? (fixedEnd?.occurredAt ?? null) : endIso;
  const minutes = shiftStart && shiftEnd ? Math.round((Date.parse(shiftEnd) - Date.parse(shiftStart)) / 60_000) : null;
  const summary =
    shiftStart && shiftEnd && minutes !== null && minutes > 0
      ? t('attendance.shiftSummary', {
          start: moment(shiftStart),
          end: moment(shiftEnd),
          duration: t('attendance.duration', splitHours(minutes)),
          day: dayLabel(localDateAndMinutes(shiftStart, tz).date, lang, false),
        })
      : kind === 'in' && shiftStart && !shiftEnd
        ? t('attendance.shiftOpenSummary', { start: moment(shiftStart) })
        : null;

  const showEndSession = kind === 'out' && fixedStart !== null && context.data?.openShiftPunchId === fixedStart.id;
  const note = reason === 'other' ? otherNote.trim() : t(REASONS.find((r) => r.key === reason)!.label);

  // 时间选项只给补单张的那一端：他平时、现在、几个常见整点
  const editingType = kind === 'in' ? PunchType.IN : PunchType.OUT;
  const setEditing = kind === 'in' ? setStart : setEnd;
  const editingTime = kind === 'in' ? startTime : endTime;
  const editingDate = kind === 'in' ? startDate : endDate;
  const usualTime = kind === 'in' ? usual?.clockIn : usual?.clockOut;
  const choices: Array<{ key: string; label: string; selected: boolean; apply: () => void }> = [];
  const addChoice = (value: string, label: string, date?: string) => {
    if (choices.some((c) => c.key === value)) return;
    choices.push({
      key: value,
      label,
      selected: editingTime === value && (date === undefined || editingDate === date),
      // 「现在」连日期一起定成今天；其余只换时间，日期照规则跟着推
      apply: () => setEditing((v) => (date === undefined ? { ...v, time: value } : { date, time: value })),
    });
  };
  if (usualTime) {
    addChoice(
      usualTime.time,
      t(usualTime.basis === 'person' ? 'attendance.usualPerson' : 'attendance.usualSite', {
        time: formatWallClock(usualTime.time, lang),
      }),
    );
  }
  addChoice(nowTime, t('attendance.nowAt', { time: formatWallClock(nowTime, lang) }), today);
  for (const value of FIXED_TIMES[editingType]) addChoice(value, formatWallClock(value, lang));

  const submit = useMutation({
    mutationFn: async () => {
      const common = { siteId: site.id, userId: userId!, note };
      const punch =
        kind === 'shift'
          ? await attendanceApi.manual({ ...common, type: PunchType.IN, occurredAt: startIso!, endAt: endIso! })
          : kind === 'out'
            ? await attendanceApi.manual({
                ...common,
                type: PunchType.OUT,
                occurredAt: endIso!,
                // 服务端没收到 endSession 时按「结束」处理，所以不该结束时要明确传 false
                endSession: showEndSession && endSession,
              })
            : await attendanceApi.manual({ ...common, type: PunchType.IN, occurredAt: startIso! });
      if (anomaly?.alsoResolve && alsoResolve) {
        await attendanceApi.resolveAnomaly(anomaly.alsoResolve.id, note);
      }
      return punch;
    },
    onSuccess: () => {
      invalidateAttendance(queryClient);
      onClose();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const canSubmit = userId !== null && context.isSuccess && ready && problem === null && note.length > 0;

  const pickUser = (id: number | null) => {
    setUserId(id);
    setFreeKind(null);
    setStart(UNTOUCHED);
    setEnd(UNTOUCHED);
  };

  // 时间框至少 10rem，浏览器量出来更宽就跟着撑。英文界面、手机设成 12 小时制时它显示成「05:05 PM」，
  // 原来写死 w-32（128px），pad 上 AM / PM 被截掉（客户反馈「自定义时间框看不见AM和PM被遮挡了」）
  const momentInputs = (
    value: { date: string; time: string },
    setValue: Dispatch<SetStateAction<MomentInput>>,
    label: string,
  ) => (
    <span className="flex flex-wrap items-center gap-2">
      <Input
        type="date"
        required
        max={today}
        value={value.date}
        onChange={(e) => setValue((v) => ({ ...v, date: e.target.value }))}
        aria-label={`${label} · ${t('attendance.date')}`}
        className="w-auto min-w-40 font-mono"
      />
      <Input
        type="time"
        required
        value={value.time}
        onChange={(e) => setValue((v) => ({ ...v, time: e.target.value }))}
        aria-label={label}
        className="w-auto min-w-40 text-center font-mono text-title"
      />
    </span>
  );

  const fixedMoment = (punch: ContextPunch | null) =>
    punch ? (
      <span className="font-mono text-body tabular-nums text-ink">{moment(punch.occurredAt)}</span>
    ) : (
      <span className="rounded border border-dashed border-danger px-1.5 text-caption text-danger">
        {t('attendance.notPunched')}
      </span>
    );

  const endRow = (label: string, content: ReactNode) => (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="w-14 shrink-0 text-body text-ink-3">{label}</span>
      {content}
    </div>
  );

  const problemText =
    problem === null
      ? null
      : problem === 'tooLong'
        ? t('attendance.checkTooLong', { hours: maxShiftMinutes / 60 })
        : t(PROBLEM_LABEL[problem]);

  const submitLabel =
    kind === 'shift' && startIso && endIso
      ? t('attendance.submitShift', { start: shortMoment(startIso), end: shortMoment(endIso) })
      : kind === 'out' && endIso
        ? t('attendance.submitOut', { time: shortMoment(endIso) })
        : kind === 'in' && startIso
          ? t('attendance.submitIn', { time: shortMoment(startIso) })
          : t(KIND_LABEL[kind]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (canSubmit) submit.mutate();
      }}
      className="flex flex-col gap-4"
    >
      {error && <Alert>{error}</Alert>}

      {free && (
        <div className="flex flex-col gap-2">
          <span className="text-body text-ink-3">{t('attendance.backfillWho')}</span>
          {onShiftPeople.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {onShiftPeople.map((person) => (
                <Choice key={person.user.id} selected={userId === person.user.id} onClick={() => pickUser(person.user.id)}>
                  {person.user.name} ·{' '}
                  {t('attendance.onShiftAt', { time: formatClock(person.onShiftSince!, tz, lang) })}
                </Choice>
              ))}
            </div>
          )}
          <Select
            aria-label={t('attendance.otherPeople')}
            value={userId ?? ''}
            onChange={(e) => pickUser(/^\d+$/.test(e.target.value) ? Number(e.target.value) : null)}
            className="sm:max-w-xs"
          >
            <option value="">{t('attendance.otherPeople')}</option>
            {(members.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.employeeNo} · {m.name}
              </option>
            ))}
          </Select>
        </div>
      )}

      {userId !== null && context.isLoading && (
        <div className="flex justify-center py-4 text-ink-3">
          <Spinner />
        </div>
      )}
      {context.isError && <Alert>{toMessage(context.error)}</Alert>}

      {userId !== null && context.isSuccess && (
        <>
          {free && (
            <div className="flex flex-wrap gap-2">
              {(['out', 'in', 'shift'] as const).map((option) => (
                <Choice
                  key={option}
                  selected={kind === option}
                  onClick={() => {
                    setFreeKind(option);
                    setStart(UNTOUCHED);
                    setEnd(UNTOUCHED);
                  }}
                >
                  {t(KIND_LABEL[option])}
                </Choice>
              ))}
            </div>
          )}

          <div className="flex max-w-xl flex-col gap-2">
            {endRow(
              t('attendance.startLabel'),
              kind === 'out'
                ? fixedMoment(fixedStart)
                : momentInputs({ date: startDate, time: startTime }, setStart, t('attendance.addIn')),
            )}
            {endRow(
              t('attendance.endLabel'),
              kind === 'in'
                ? fixedMoment(fixedEnd)
                : momentInputs({ date: endDate, time: endTime }, setEnd, t('attendance.addOut')),
            )}
            {summary && <p className="text-body text-ink-2">{summary}</p>}
          </div>

          {kind !== 'shift' && (
            <div className="flex flex-wrap gap-2">
              {choices.map((choice) => (
                <Choice key={choice.key} selected={choice.selected} onClick={choice.apply}>
                  {choice.label}
                </Choice>
              ))}
            </div>
          )}

          {problemText && (
            <div className="flex flex-wrap items-center gap-2 text-body text-danger">
              <span>{problemText}</span>
              {suggestion && (
                <Button type="button" variant="ghost" onClick={() => setEditing((v) => ({ ...v, time: suggestion }))}>
                  {t('attendance.checkSuggest', { time: formatWallClock(suggestion, lang) })}
                </Button>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-body text-ink-3">{t('attendance.whyMissed')}</span>
            <div className="flex flex-wrap gap-2">
              {REASONS.map((option) => (
                <Choice key={option.key} selected={reason === option.key} onClick={() => setReason(option.key)}>
                  {t(option.label)}
                </Choice>
              ))}
            </div>
            {reason === 'other' && (
              <Input
                value={otherNote}
                onChange={(e) => setOtherNote(e.target.value)}
                placeholder={t('attendance.reasonOtherPlaceholder')}
                aria-label={t('attendance.reasonOther')}
                maxLength={500}
                autoFocus
              />
            )}
          </div>

          {showEndSession ? (
            <Checkbox
              checked={endSession}
              onChange={(e) => setEndSession(e.target.checked)}
              label={t('attendance.endSession')}
              hint={t('attendance.endSessionHint')}
            />
          ) : (
            kind === 'out' &&
            fixedStart !== null && <p className="text-body text-ink-3">{t('attendance.endSessionPast')}</p>
          )}

          {anomaly?.alsoResolve && (
            <Checkbox
              checked={alsoResolve}
              onChange={(e) => setAlsoResolve(e.target.checked)}
              label={t('attendance.alsoResolve', { time: formatClock(anomaly.alsoResolve.occurredAt, tz, lang) })}
            />
          )}

          <FormActions
            className={cn('sm:ml-auto', kind === 'shift' ? 'sm:w-[34rem]' : 'sm:w-96')}
            submitLabel={submitLabel}
            cancelLabel={t('app.cancel')}
            onCancel={onClose}
            loading={submit.isPending}
            disabled={!canSubmit}
          />
        </>
      )}

      {(userId === null || !context.isSuccess) && (
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('app.cancel')}
          </Button>
        </div>
      )}
    </form>
  );
}
